"use client";

import { useEffect, useState, useCallback } from "react";
import { AeraButton } from "@aera/ui";
import { X } from "lucide-react";

export type ToastVariant = "success" | "error";

export type ToastAction = {
  label: string;
  onClick: () => void | Promise<void>;
};

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  action?: ToastAction;
  exiting?: boolean;
}

const TOAST_EVENT = "aera:toast";
let toastIdCounter = 0;

export function showToast(
  message: string,
  variant: ToastVariant = "success",
  action?: ToastAction,
  durationMs?: number,
) {
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, {
      detail: { message, variant, action, durationMs },
    }),
  );
}

/** Success toast with Undo action — stays longer while action is available. */
export function showUndoToast(
  message: string,
  onUndo: () => void | Promise<void>,
) {
  showToast(message, "success", {
    label: "ย้อนกลับ",
    onClick: onUndo,
  });
}

const VARIANT_STYLES: Record<
  ToastVariant,
  {
    container: string;
    icon: string;
    iconPath: string;
    text: string;
    closeBtn: string;
  }
> = {
  success: {
    container: "bg-aera-greenlight",
    icon: "text-aera-green",
    iconPath: "M5 13l4 4L19 7",
    text: "text-aera-green",
    closeBtn: "text-aera-green hover:bg-aera-green hover:text-aera-white",
  },
  error: {
    container: "bg-aera-redlight",
    icon: "text-aera-red",
    iconPath: "M6 18L18 6M6 6l12 12",
    text: "text-aera-red",
    closeBtn: "text-aera-red hover:bg-aera-red hover:text-aera-white",
  },
};

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback(
    (
      message: string,
      variant: ToastVariant = "success",
      action?: ToastAction,
      durationMs?: number,
    ) => {
      const id = ++toastIdCounter;
      setToasts((prev) => [...prev, { id, message, variant, action }]);

      const duration =
        durationMs ?? (action ? 8000 : variant === "success" ? 2000 : 4000);
      setTimeout(() => {
        dismiss(id);
      }, duration);
    },
    [dismiss],
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const { message, variant, action, durationMs } = (
        e as CustomEvent<{
          message: string;
          variant?: ToastVariant;
          action?: ToastAction;
          durationMs?: number;
        }>
      ).detail;
      addToast(message, variant, action, durationMs);
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-99999 flex flex-col items-end gap-2">
      {toasts.map((t) => {
        const s = VARIANT_STYLES[t.variant];
        return (
          <div
            key={t.id}
            style={{
              animation: t.exiting
                ? "toast-exit 0.3s ease-in forwards"
                : "toast-enter 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards",
            }}
            className={`pointer-events-auto flex w-[min(420px,calc(100vw-2rem))] items-center gap-3 rounded-2xl px-4 py-3 backdrop-blur-sm ${s.container}`}
          >
            <svg
              className={`h-4.5 w-4.5 shrink-0 ${s.icon}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d={s.iconPath}
              />
            </svg>
            <span className={`min-w-0 flex-1 text-sm font-medium ${s.text}`}>
              {t.message}
            </span>
            {t.action ? (
              <AeraButton
                variant="ghost"
                size="xs"
                type="button"
                onClick={() => {
                  void t.action?.onClick();
                  dismiss(t.id);
                }}
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold underline-offset-2 hover:underline ${s.text}`}
              >
                {t.action.label}
              </AeraButton>
            ) : null}
            <AeraButton
              variant="ghost"
              size="xs"
              type="button"
              paddingSize="none"
              onClick={() => dismiss(t.id)}
              className={`h-7 w-7 shrink-0 rounded-full ${s.closeBtn}`}
              icon={<X className="h-3.5 w-3.5" strokeWidth={2} />}
            ></AeraButton>
          </div>
        );
      })}
    </div>
  );
}
